import { useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import TopologyGraphRenderer from '@app/components/topology/TopologyGraphRenderer';
import type { TopologyV2Graphschema } from '@app/types/sdk';

declare global {
  interface Window {
    __REAP_TOPOLOGY_RENDER_READY__?: boolean;
    __REAP_TOPOLOGY_RENDER_ERROR__?: string;
  }
}

type RenderJobPayload = {
  graph: TopologyV2Graphschema;
  title?: string;
  width: number;
  height: number;
  backgroundColor?: string;
};

const waitFrame = () => new Promise<void>((resolve) => window.requestAnimationFrame(() => resolve()));

const InternalTopologyRenderPage = () => {
  const { jobId } = useParams();
  const location = useLocation();
  const [payload, setPayload] = useState<RenderJobPayload | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    window.__REAP_TOPOLOGY_RENDER_READY__ = false;
    window.__REAP_TOPOLOGY_RENDER_ERROR__ = '';
    if (!jobId) {
      const message = 'Missing topology render job id.';
      setError(message);
      window.__REAP_TOPOLOGY_RENDER_ERROR__ = message;
      return;
    }
    const accessToken = new URLSearchParams(location.search).get('token')?.trim() ?? '';
    const abort = new AbortController();
    const fetchPath = accessToken
      ? `/internal/v1/topology/render-jobs/${encodeURIComponent(jobId)}?token=${encodeURIComponent(accessToken)}`
      : `/internal/v1/topology/render-jobs/${encodeURIComponent(jobId)}`;
    fetch(fetchPath, {
      signal: abort.signal
    })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`Failed to load topology render job (${response.status})`);
        }
        return (await response.json()) as RenderJobPayload;
      })
      .then((next) => {
        setPayload(next);
        setError(null);
      })
      .catch((fetchError) => {
        if (abort.signal.aborted) {
          return;
        }
        const message = fetchError instanceof Error ? fetchError.message : 'Failed to load topology render job.';
        setError(message);
        window.__REAP_TOPOLOGY_RENDER_ERROR__ = message;
      });
    return () => {
      abort.abort();
    };
  }, [jobId, location.search]);

  useEffect(() => {
    if (!payload || error) {
      return;
    }
    let cancelled = false;

    const markReady = async () => {
      try {
        if (typeof document !== 'undefined' && 'fonts' in document) {
          await (document as Document & { fonts?: FontFaceSet }).fonts?.ready?.catch?.(() => undefined);
        }
      } catch {
        // best-effort only
      }
      await waitFrame();
      await waitFrame();
      window.setTimeout(() => {
        if (cancelled) {
          return;
        }
        window.__REAP_TOPOLOGY_RENDER_READY__ = true;
      }, 180);
    };

    void markReady();

    return () => {
      cancelled = true;
      window.__REAP_TOPOLOGY_RENDER_READY__ = false;
    };
  }, [payload, error]);

  const dimensions = useMemo(
    () => ({
      width: payload?.width ?? 1600,
      height: payload?.height ?? 900
    }),
    [payload]
  );

  const backgroundColor = payload?.backgroundColor ?? '#f8fafc';
  const title = payload?.title ?? 'Topology export';

  return (
    <main
      data-topology-render-root="true"
      style={{
        width: dimensions.width,
        height: dimensions.height,
        minWidth: dimensions.width,
        minHeight: dimensions.height,
        background: backgroundColor,
        overflow: 'hidden',
        display: 'grid',
        gridTemplateRows: 'auto 1fr',
        boxSizing: 'border-box',
        padding: 24,
        gap: 16
      }}>
      <header
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
          color: '#0f172a',
          fontFamily: '"DM Sans", system-ui, sans-serif'
        }}>
        <div style={{ fontSize: 24, fontWeight: 700 }}>{title}</div>
        {payload?.graph?.scope ? (
          <div style={{ fontSize: 13, opacity: 0.72 }}>
            {payload.graph.scope.scopeType}:{payload.graph.scope.scopeId}
          </div>
        ) : null}
      </header>
      <section
        style={{
          position: 'relative',
          minHeight: 0,
          borderRadius: 20,
          overflow: 'hidden',
          background: '#ffffff',
          border: '1px solid rgba(15, 23, 42, 0.08)'
        }}>
        {error ? (
          <div
            style={{
              display: 'grid',
              placeItems: 'center',
              width: '100%',
              height: '100%',
              color: '#991b1b',
              fontFamily: '"DM Sans", system-ui, sans-serif'
            }}>
            {error}
          </div>
        ) : payload ? (
          <TopologyGraphRenderer topology={payload.graph} graphKey={payload.graph.graphId} showControls={false} />
        ) : (
          <div
            style={{
              display: 'grid',
              placeItems: 'center',
              width: '100%',
              height: '100%',
              color: '#475569',
              fontFamily: '"DM Sans", system-ui, sans-serif'
            }}>
            Loading topology render…
          </div>
        )}
      </section>
    </main>
  );
};

export default InternalTopologyRenderPage;
